export default {
  name: 'company',
  subtitle: 'Companies are the groups and individuals writing the Guides',
  uniqKey: '_id',
  crudMapping: {
    readMany: 'companies',
    readOne: 'company',
    update: 'updateCompany',
  },
  readManySchema: {
    showTotal: true,
    cellFormatter(value, object, fieldName) {
      if (fieldName === 'freeSubscriptionUserIds') {
        return value.length;
      }
      return value;
    },
    jsonSchema: {
      type: 'object',
      properties: {
        name: {
          type: 'string',
        },
        freeSubscriptionUserIds: {
          type: 'number',
          title: 'Free subscribers',
        },
      },
    },
  },
  defaultSchema: {
    jsonSchema: {
      title: 'Company',
      type: 'object',
      required: ['name', 'slug'],
      properties: {
        name: {
          type: 'string',
          title: 'Company Name',
        },
        subtitle: {
          type: 'string',
          title: 'Subtitle',
          maxLength: 180,
        },
        slug: {
          type: 'string',
          title: 'URL Slug',
        },
        curatorLandingLink: {
          type: 'string',
          title: 'Encouragex Landing Page (Copy and paste this link to share)',
        },
        description: {
          type: 'string',
          title: 'Description',
        },
        galleries: {
          type: 'array',
          title: 'Galleries',
          description: 'Galleries to group up videos and images',
          items: {
            type: 'object',
            required: ['sortIndex'],
            properties: {
              name: {
                type: 'string',
                title: 'Name',
              },
              description: {
                type: 'string',
                title: 'Subtitle',
              },
              sortIndex: {
                type: 'number',
                title: 'Major Sort Index',
                description:
                  'Input number in all Major Sort Index fields if you wish to rearrenge order in page',
              },
              companyVideos: {
                type: 'array',
                title: 'Videos',
                description:
                  'These videos will appear in the selected Gallery.',
                items: {
                  type: 'object',
                  required: ['src', 'sortIndex'],
                  properties: {
                    src: {
                      type: 'string',
                      title: 'Video Url',
                    },
                    caption: {
                      type: 'string',
                      title: 'Video Caption',
                    },
                    sortIndex: {
                      type: 'number',
                      title: 'Sort index',
                      default: 0,
                    },
                  },
                },
              },
              companyImages: {
                type: 'array',
                title: 'Images',
                description:
                  'These images will appear in the selected Gallery.',
                default: [],
                items: {
                  type: 'object',
                  required: ['src', 'sortIndex'],
                  properties: {
                    src: {
                      type: 'string',
                      title: 'Image File',
                    },
                    caption: {
                      type: 'string',
                      title: 'Image Caption',
                    },
                    url: {
                      type: 'string',
                      title: 'Image Link',
                    },
                    sortIndex: {
                      type: 'number',
                      title: 'Sort index',
                      default: 0,
                    },
                  },
                },
              },
            },
          },
        },
        private: {
          type: 'boolean',
          title: 'Private Company',
          description: 'Hide from collection search',
          default: false,
        },
        headerImageId: {
          type: 'string',
          title: 'Header Image',
          hint: 'Please upload a high definition image',
          description: 'This image will appear as as the header image for the landing page.',
        },
        lifeEventThemeIds: {
          type: 'array',
          default: [],
          items: {
            type: 'string',
          },
          title: 'Associated life event themes',
        },
        curatorHeadline: {
          type: 'string',
          title: 'Curator Headline',
          description: 'This text will appear as the title for the Curator section',
          maxLength: 75,
        },
        curators: {
          type: 'array',
          title: 'Featured Curators',
          description: 'Curators to be displayed in the company landing page (max. 5)',
          default: [],
          items: {
            type: 'object',
            required: ['name', 'image'],
            properties: {
              name: {
                type: 'string',
                title: 'Name',
              },
              description: {
                type: 'string',
                title: 'Description',
              },
              image: {
                type: 'string',
                title: 'Image',
              },
            },
          },
          maxItems: 5,
        },
        subscriptionPlan: {
          type: 'object',
          title: 'Subscription Plan',
          properties: {
            recurlyId: {
              type: 'string',
              title: 'Recurly ID',
            },
            pricing: {
              title: 'Pricing',
              type: 'object',
              required: ['cost', 'period', 'periodAmount'],
              properties: {
                cost: {
                  title: 'Monthly Subscription Cost',
                  type: 'number',
                  default: 0,
                },
                period: {
                  title: 'Period',
                  type: 'string',
                  description: 'Unit of time on which the user will be charged',
                  enum: ['months', 'days'],
                  default: 'months',
                  enumNames: ['Months', 'Days'],
                },
                periodAmount: {
                  title: 'Number of Periods',
                  type: 'number',
                  default: 1,
                  description:
                    'Number of period units between each subscription charge (number of months or number of days)',
                },
              },
            },
          },
        },
      },
    },
    uiSchema: {
      curatorLandingLink: {
        'ui:readonly': true,
      },
      subscriptionPlan: {
        recurlyId: {
          'ui:readonly': true,
        },
        pricing: {
          period: {
            'ui:readonly': true,
            'ui:widget': 'hidden',
          },
          periodAmount: {
            'ui:readonly': true,
            'ui:widget': 'hidden',
          },
        },
      },
      headerImageId: {
        'ui:widget': 'imageWidget',
      },
      subtitle: {
        'ui:widget': 'textarea',
        'ui:options': {
          rows: 5,
        },
      },
      description: {
        'ui:widget': 'wysiwygWidget',
        'ui:options': {
          wysiwygConfig: {
            toolbar: {
              options: ['link', 'list', 'inline', 'textAlign'],
              link: {
                showOpenOptionOnHover: false,
              },
              list: {
                options: ['unordered'],
              },
              inline: {
                inDropdown: false,
                options: ['bold', 'italic'],
              },
              textAlign: {
                inDropdown: false,
                options: ['left', 'center', 'right', 'justify'],
              },
            },
            editorClassName: 'form-control',
          },
          blockType: 'unordered-list-item',
        },
      },
      slug: {
        'ui:widget': 'slugWidget',
      },
      lifeEventThemeIds: {
        'ui:field': 'hasManyField',
      },
      curators: {
        items: {
          image: {
            'ui:widget': 'imageWidget',
          },
        },
      },
    },
  },
  updateSchema: {
    jsonSchema: {
      title: 'Company',
      type: 'object',
      required: ['name', 'slug'],
      properties: {
        name: {
          type: 'string',
          title: 'Company Name',
        },
        subtitle: {
          type: 'string',
          title: 'Subtitle',
          maxLength: 180,
        },
        slug: {
          type: 'string',
          title: 'URL Slug',
        },
        curatorLandingLink: {
          type: 'string',
          title: 'Encouragex Landing Page (Copy and paste this link to share)',
        },
        description: {
          type: 'string',
          title: 'Description',
        },
        galleries: {
          type: 'array',
          title: 'Galleries',
          description: 'Galleries to group up videos and images',
          items: {
            type: 'object',
            required: ['sortIndex'],
            properties: {
              name: {
                type: 'string',
                title: 'Name',
              },
              description: {
                type: 'string',
                title: 'Subtitle',
              },
              sortIndex: {
                type: 'number',
                title: 'Major Sort Index',
                description:
                  'Input number in all Major Sort Index fields if you wish to rearrenge order in page',
              },
              companyVideos: {
                type: 'array',
                title: 'Videos',
                description:
                  'These videos will appear in the selected Gallery.',
                items: {
                  type: 'object',
                  required: ['src', 'sortIndex'],
                  properties: {
                    src: {
                      type: 'string',
                      title: 'Video Url',
                    },
                    caption: {
                      type: 'string',
                      title: 'Video Caption',
                    },
                    sortIndex: {
                      type: 'number',
                      title: 'Sort index',
                      default: 0,
                    },
                  },
                },
              },
              companyImages: {
                type: 'array',
                title: 'Images',
                description:
                  'These images will appear in the selected Gallery.',
                default: [],
                items: {
                  type: 'object',
                  required: ['src', 'sortIndex'],
                  properties: {
                    src: {
                      type: 'string',
                      title: 'Image File',
                    },
                    caption: {
                      type: 'string',
                      title: 'Image Caption',
                    },
                    url: {
                      type: 'string',
                      title: 'Image Link',
                    },
                    sortIndex: {
                      type: 'number',
                      title: 'Sort index',
                      default: 0,
                    },
                  },
                },
              },
            },
          },
        },
        private: {
          type: 'boolean',
          title: 'Private Company',
          description: 'Hide from collection search',
          default: false,
        },
        headerImageId: {
          type: 'string',
          title: 'Header Image',
          hint: 'Please upload a high definition image',
          description: 'This image will appear as as the header image for the landing page.',
        },
        lifeEventThemeIds: {
          type: 'array',
          default: [],
          items: {
            type: 'string',
          },
          title: 'Associated life event themes',
        },
        curatorHeadline: {
          type: 'string',
          title: 'Curator Headline',
          description: 'This text will appear as the title for the Curator section',
          maxLength: 75,
        },
        curators: {
          type: 'array',
          title: 'Featured Curators',
          description: 'Curators to be displayed in the company landing page (max. 5)',
          default: [],
          items: {
            type: 'object',
            required: ['name', 'image'],
            properties: {
              name: {
                type: 'string',
                title: 'Name',
              },
              description: {
                type: 'string',
                title: 'Description',
              },
              image: {
                type: 'string',
                title: 'Image',
              },
            },
          },
          maxItems: 5,
        },
        subscriptionPlan: {
          type: 'object',
          title: 'Subscription Plan',
          properties: {
            recurlyId: {
              type: 'string',
              title: 'Recurly ID',
            },
            pricing: {
              title: 'Pricing',
              type: 'object',
              required: ['cost', 'period', 'periodAmount'],
              properties: {
                cost: {
                  title: 'Monthly Subscription Cost',
                  type: 'number',
                  default: 0,
                },
                period: {
                  title: 'Period',
                  type: 'string',
                  description: 'Unit of time on which the user will be charged',
                  enum: ['months', 'days'],
                  default: 'months',
                  enumNames: ['Months', 'Days'],
                },
                periodAmount: {
                  title: 'Number of Periods',
                  type: 'number',
                  default: 1,
                  description:
                    'Number of period units between each subscription charge (number of months or number of days)',
                },
              },
            },
          },
        },
        featureGroups: {
          title: 'Featured Curators Groups',
          description: 'Collections of featured Curators',
          type: 'array',
          items: {
            type: 'string',
          },
          default: [],
        },
      },
    },
    uiSchema: {
      curatorLandingLink: {
        'ui:readonly': true,
      },
      subscriptionPlan: {
        recurlyId: {
          'ui:readonly': true,
        },
        pricing: {
          cost: {
            'ui:readonly': true,
          },
          period: {
            'ui:readonly': true,
            'ui:widget': 'hidden',
          },
          periodAmount: {
            'ui:readonly': true,
            'ui:widget': 'hidden',
          },
        },
      },
      headerImageId: {
        'ui:widget': 'imageWidget',
      },
      subtitle: {
        'ui:widget': 'textarea',
        'ui:options': {
          rows: 5,
        },
      },
      galleries: {
        items: {
          companyVideos: {
            classNames: 'nested-field',
            'ui:options': {
              orderable: false,
            },
            items: {
              src: {
                'ui:widget': 'videoUrlWidget',
              },
              caption: {
                'ui:widget': 'textarea',
                'ui:options': {
                  rows: 1,
                },
              },
            },
          },
          companyImages: {
            classNames: 'nested-field',
            'ui:options': {
              orderable: false,
            },
            items: {
              src: {
                'ui:widget': 'imageWidget',
              },
              caption: {
                'ui:widget': 'textarea',
                'ui:options': {
                  rows: 1,
                },
              },
            },
          },
        },
      },
      description: {
        'ui:widget': 'wysiwygWidget',
        'ui:options': {
          wysiwygConfig: {
            toolbar: {
              options: ['link', 'list', 'inline', 'textAlign'],
              link: {
                showOpenOptionOnHover: false,
              },
              list: {
                options: ['unordered'],
              },
              inline: {
                inDropdown: false,
                options: ['bold', 'italic'],
              },
              textAlign: {
                inDropdown: false,
                options: ['left', 'center', 'right', 'justify'],
              },
            },
            editorClassName: 'form-control',
          },
          blockType: 'unordered-list-item',
        },
      },
      slug: {
        'ui:widget': 'slugWidget',
      },
      lifeEventThemeIds: {
        'ui:field': 'hasManyField',
      },
      featureGroups: {
        'ui:widget': 'hidden',
      },
      curators: {
        items: {
          image: {
            'ui:widget': 'imageWidget',
          },
        },
      },
    },
  },
};



// WEBPACK FOOTER //
// ./src/schemas/curator/company.js